/**
 * Hides the display-only Free Livefood Shaker cart row when the logged-in
 * customer already used the first-subscription grant (CustomerPromoGrant).
 *
 * Calls App Proxy: GET /apps/viperia-integration/shaker-promo/eligibility
 * (Shopify signs logged_in_customer_id). Caches result in sessionStorage
 * keyed by shop + customer id (TTL) so cart-drawer re-renders can hide
 * immediately without a flash.
 *
 * Fail mode (logged-in only):
 * - On API error, fail-closed (keep row hidden) unless a prior positive
 *   eligible cache exists for this shop+customer.
 * - Guests never hit this script path; liquid shows cautious copy.
 */
(function () {
  const PATH = '/apps/viperia-integration/shaker-promo/eligibility';
  const ROW_SELECTOR =
    '[data-shaker-promo-row][data-shaker-promo-check-eligibility="true"]';
  const CACHE_PREFIX = 'viperia_shaker_promo_eligible';
  const LEGACY_CACHE_KEY = 'viperia_shaker_promo_eligible';
  const CACHE_TTL_MS = 5 * 60 * 1000;
  let inflight = null;

  function apply(eligible) {
    document.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      if (eligible) {
        row.hidden = false;
        row.removeAttribute('hidden');
      } else {
        row.hidden = true;
        row.setAttribute('hidden', '');
      }
    });
  }

  function getIdentity() {
    const row = document.querySelector(ROW_SELECTOR);
    if (!row) return null;
    const shop = (row.getAttribute('data-shaker-promo-shop') || '').trim();
    const customerId = (
      row.getAttribute('data-shaker-promo-customer-id') || ''
    ).trim();
    if (!shop || !customerId) return null;
    return { shop, customerId };
  }

  function cacheKey(identity) {
    return CACHE_PREFIX + ':' + identity.shop + ':' + identity.customerId;
  }

  function readCacheEntry(identity, { allowExpired } = { allowExpired: false }) {
    if (!identity) return null;
    try {
      const raw = sessionStorage.getItem(cacheKey(identity));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return null;
      if (typeof parsed.eligible !== 'boolean' || typeof parsed.ts !== 'number') {
        return null;
      }
      if (!allowExpired && Date.now() - parsed.ts > CACHE_TTL_MS) {
        return null;
      }
      return parsed;
    } catch (_err) {
      /* private mode / bad JSON */
    }
    return null;
  }

  function writeCache(identity, eligible) {
    if (!identity) return;
    try {
      sessionStorage.removeItem(LEGACY_CACHE_KEY);
      sessionStorage.setItem(
        cacheKey(identity),
        JSON.stringify({ eligible: !!eligible, ts: Date.now() })
      );
    } catch (_err) {
      /* private mode */
    }
  }

  function clearCache(identity) {
    if (!identity) return;
    try {
      sessionStorage.removeItem(cacheKey(identity));
      sessionStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (_err) {
      /* private mode */
    }
  }

  function fetchEligibility(identity) {
    if (inflight) return inflight;
    inflight = fetch(PATH, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((res) => {
        if (!res.ok) throw new Error('eligibility_http_' + res.status);
        return res.json();
      })
      .then((data) => {
        const eligible = data && data.eligible === true;
        writeCache(identity, eligible);
        apply(eligible);
        return eligible;
      })
      .catch(() => {
        // Logged-in fail-closed: keep hidden unless we previously knew eligible.
        // Tradeoff: brief outages may hide a first-grant banner; better than
        // flashing the promo at returning subscribers. Guests are unchanged
        // (liquid shows cautious copy; they do not use this path).
        const prior = readCacheEntry(identity, { allowExpired: true });
        if (prior && prior.eligible === true) {
          apply(true);
          return true;
        }
        apply(false);
        return false;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  }

  function sync({ refetch } = { refetch: false }) {
    if (!document.querySelector(ROW_SELECTOR)) return;

    const identity = getIdentity();
    if (!identity) {
      // Missing shop/customer attrs — keep liquid default (hidden) for safety.
      apply(false);
      return;
    }

    const cached = readCacheEntry(identity);
    if (cached !== null) {
      apply(cached.eligible);
      if (!refetch) return;
    } else if (refetch) {
      clearCache(identity);
    }

    fetchEligibility(identity);
  }

  function init() {
    sync({ refetch: false });
    [100, 500, 1500].forEach((ms) => setTimeout(() => sync({ refetch: false }), ms));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      setTimeout(() => sync({ refetch: true }), 100);
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      sync({ refetch: true });
    }
  });
})();
